/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight, FileText, Mic2, Video } from "lucide-react";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

import { careerStory } from "./career-story";

export const HeroSection = () => {
  const { user } = useAuthStore();
  const reduced = useReducedMotion();
  return (
    <section className="folio-hero hero-product-first">
      <div className="folio-shell folio-hero-grid">
        <div>
          <p className="folio-eyebrow">Spotlight Intro + Practice Interview</p>
          <h1>
            <span>Show who you are.</span> <span>Practice what comes next.</span>
          </h1>
          <p className="folio-lead">
            Record an AI-guided video introduction that recruiters can watch alongside your resume.
            Practice out loud with an AI interviewer that listens and gives feedback.
          </p>
          <div className="folio-actions">
            <Link
              className="folio-button"
              to={user ? "/dashboard/spotlight-intro" : "/auth/register"}
            >
              <Video size={17} aria-hidden="true" /> Create Spotlight Intro
            </Link>
            <Link
              className="folio-button folio-button-ink"
              to={user ? "/dashboard/practice-interview" : "/practice-interview"}
            >
              <Mic2 size={17} aria-hidden="true" /> Practice Interview
            </Link>
          </div>
          <Link className="hero-resume-link" to={user ? "/dashboard" : "/auth/register"}>
            Build and tailor your resume, too <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
        <div
          className="hero-voice-stage"
          aria-label="Spotlight Intro and Practice Interview examples"
        >
          <motion.a
            href="#spotlight-intros"
            aria-label="Explore Spotlight Intro"
            className="hero-voice-preview hero-spotlight-preview"
            initial={reduced ? false : { opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="hero-preview-heading">
              <span className="folio-label">
                <Video size={15} /> Spotlight Intro
              </span>
              <ArrowDown size={17} aria-hidden="true" />
            </div>
            <div className="hero-intro-scene">
              <img
                src={careerStory.portrait}
                alt="Illustrated example candidate Avery Morgan"
                width="800"
                height="1000"
                loading="eager"
              />
              <div>
                <h2>Your story. In your own words.</h2>
                <p>“{careerStory.decision}”</p>
                <span className="hero-preview-detail">AI-guided recording · Saved transcript</span>
              </div>
            </div>
          </motion.a>
          <motion.a
            href="#practice-interviews"
            aria-label="Explore Practice Interview"
            className="hero-voice-preview hero-practice-preview"
            initial={reduced ? false : { opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="hero-preview-heading">
              <span className="folio-label">
                <Mic2 size={15} /> Practice Interview
              </span>
              <ArrowDown size={17} aria-hidden="true" />
            </div>
            <h2>A conversation, not a script.</h2>
            <p>“{careerStory.question}”</p>
            <div className="hero-practice-turn">
              <span className="hero-voice-wave" aria-hidden="true">
                {[10, 19, 28, 14, 34, 23, 12, 29, 18, 9].map((height, index) => (
                  <span key={index} style={{ height }} />
                ))}
              </span>
              <span className="hero-preview-detail">
                Speak naturally. Get feedback on your answers.
              </span>
            </div>
          </motion.a>
          <div className="hero-voice-foundation">
            <FileText size={17} aria-hidden="true" />
            <span>Your resume is the foundation. Your voice adds the context.</span>
          </div>
          <p className="folio-example-note">Illustrative examples · Explore either product above</p>
        </div>
      </div>
    </section>
  );
};
